import dayjs from 'dayjs';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Image,
  Modal,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
} from 'react-native';
import { ToastProvider, useToast } from 'react-native-toast-notifications';

import Avatar from '@components/Avatar/Avatar';
import CachedImage from '@components/CachedImage/CachedImage';
import { CachedVideo } from '@components/CachedImage/CachedVideo';
import { ChatImageForwardPicker } from '@components/Chat/ChatImageForwardPicker.web';
import { CustomToast } from '@components/CustomToast/CustomToast.web';
import { Box, Text } from '@components/Restyle';
import {
  BottomToast,
  BottomToastHandle,
} from '@components/shared/BottomToast.web';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import {
  BOTTOM_H,
  chromeStyles,
  NAV_ICON,
  THUMB_STRIP_END_THRESHOLD,
  THUMB_W,
  TOOLBAR_ICON,
  ZOOM_MAX,
  ZOOM_MIN,
  ZOOM_STEP,
} from '@components/shared/mediaViewerChrome';
import { ToolbarTooltip } from '@components/shared/ToolbarTooltip.web';
import {
  Document,
  useDeleteMessageDocumentMutation,
  useDocumentsByClientIdsForImageViewerLazyQuery,
  useListProjectsTasksQuery,
  useListRecentProjectsTasksQuery,
  useUpdateDocumentTagsMutation,
  useUpdateProjectFileTagsMutation,
} from '@graphql/generated';
import { useDeleteMessage } from '@hooks/useDeleteMessage';
import { useDeleteProjectDocument } from '@hooks/useDeleteProjectDocument';
import { useDeleteTaskFiles } from '@hooks/useDeleteTaskFile';
import useDownloadFiles from '@hooks/useDownloadFiles';
import useMe from '@hooks/useMe';
import { useGlobalPointerListener } from '@hooks/useOutsideClick.web';
import { usePreviewDocuments } from '@hooks/usePreviewDocuments';
import { useRenameDocument } from '@hooks/useRenameDocument';
import { useResolveDocumentUrl } from '@hooks/useResolveDocumentUrl';
import { useShareImagesLink } from '@hooks/useShareImagesLink.web';
import Images from '@themes/images';
import theme from '@themes/theme';
import {
  CLIPBOARD_FAILURE_MESSAGE_KEYS,
  copyTextToClipboard,
  CopyTextResult,
} from '@utils/copyTextToClipboard';

const WHEEL_THROTTLE_MS = 200;

// ── Types ─────────────────────────────────────────────────────────────────────
interface Props {
  onDismiss?: () => void;
  chatId?: string;
  // Message that owns the previewed attachments. Used for delete: when the
  // message has multiple images, the viewer republishes the message via
  // updateMessageIndividual with the filtered list (per-image delete). When
  // only one image is left, the whole message is deleted via deleteMessage.
  messageId?: string;
  isViewerOnly?: boolean;
  // When provided, overrides the default onShareImages-only copy-link logic so
  // the caller can apply its own routing (e.g. createShareLink for non-media).
  onCopyLink?: (docs: Document[]) => Promise<CopyTextResult | undefined>;
  // Cursor pagination passthrough (TA-9771): when the caller's document list
  // is paginated (e.g. ProjectFiles loads `first: 30`), these let the viewer
  // request more documents as the user navigates toward the end of the loaded
  // set, instead of being capped at the loaded page.
  hasNextPage?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}

type LinkedItem = {
  id: string;
  name: string;
  type: 'project' | 'task';
  projectId?: string;
};
// HashItem and LinkedItem share the same shape — alias to avoid drift.
type HashItem = LinkedItem;
type PanOffset = { x: number; y: number };

// ── Helpers ───────────────────────────────────────────────────────────────────
// setPointerCapture throws these when the pointer is no longer active
// (TA-10848, FRONTEND-2Z6). Checked by name so a DOMException from another
// realm still matches.
function isInactivePointerCaptureError(error: unknown): boolean {
  const name = (error as { name?: unknown } | null)?.name;
  return name === 'NotFoundError' || name === 'InvalidStateError';
}

function getFilename(doc: Document): string {
  if (doc.name) return doc.name;
  try {
    const url = resolveUri(doc);
    const base = url.split('?')[0];
    const name = base.split('/').pop() ?? 'image';
    return name.includes('.') ? name : name + '.jpg';
  } catch {
    return 'image.jpg';
  }
}

function splitExt(filename: string): { base: string; ext: string } {
  const dot = filename.lastIndexOf('.');
  return dot === -1
    ? { base: filename, ext: '' }
    : { base: filename.slice(0, dot), ext: filename.slice(dot) };
}

export function resolveUri(doc: Document): string {
  if (doc.file?.url) return doc.file.url;
  if (doc.file?.cdnBaseUrl && doc.file?.path)
    return `${doc.file.cdnBaseUrl}${doc.file.path}`;
  if (doc.file?.thumbnail) return doc.file.thumbnail;
  return doc.file?.cdnBaseUrl ?? doc.file?.path ?? '';
}

// ── Component ─────────────────────────────────────────────────────────────────
export const MediaViewer: React.FC<Props> = ({
  onDismiss,
  chatId = '',
  messageId,
  isViewerOnly = false,
  onCopyLink,
  hasNextPage = false,
  loadingMore = false,
  onLoadMore,
}) => {
  const { t: formatTime } = useTranslation('format');
  const { t } = useTranslation('models');
  const toast = useToast();
  const { me } = useMe();
  const { onShareImages } = useShareImagesLink();
  const {
    previewDocuments = [],
    setPreviewDocuments,
    activeIndex,
    setRefreshList,
    tagsCollections = [],
  } = usePreviewDocuments();

  // ── Navigation state ───────────────────────────────────────────────────────
  const [idx, setIdx] = useState(activeIndex);
  useEffect(() => {
    setIdx(activeIndex);
  }, [activeIndex]);

  // True while the user pressed "next" at the last loaded document and we are
  // waiting for the next page to arrive (TA-9771).
  const [pendingAdvance, setPendingAdvance] = useState(false);
  // Guards against firing onLoadMore repeatedly for the same loaded length —
  // fetchMore doesn't flip Apollo's `loading`, so the caller can't guard it.
  const lastRequestedLenRef = useRef(-1);

  // Pagination UI (arrow past the end, strip loading cell) is only meaningful
  // when the caller wired BOTH flags — hasNextPage without onLoadMore would
  // render controls that can never load anything.
  const canLoadMore = hasNextPage && !!onLoadMore;

  const requestMore = (force = false) => {
    if (!canLoadMore || loadingMore) return;
    if (!force && lastRequestedLenRef.current === previewDocuments.length)
      return;
    lastRequestedLenRef.current = previewDocuments.length;
    onLoadMore?.();
  };

  // Fetch the next page when the thumb strip itself is scrolled to its end,
  // so reaching the trailing loading cell actually loads more instead of
  // showing a spinner that waits for arrow navigation (TA-9771).
  const onThumbStripScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (!canLoadMore || loadingMore) return;
    const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
    // Within roughly one thumbnail of the end.
    if (
      contentOffset.x + layoutMeasurement.width >=
      contentSize.width - THUMB_STRIP_END_THRESHOLD
    ) {
      requestMore();
    }
  };

  const navigateTo = (next: number) => {
    if (next < 0) return;
    if (next >= previewDocuments.length) {
      // Past the loaded page: request the next page and advance once it lands.
      // Force past the length guard so an explicit press retries a fetch that
      // previously failed for this length.
      if (canLoadMore) {
        if (!pendingAdvance) requestMore(true);
        setPendingAdvance(true);
      }
      return;
    }
    // Landing on an in-range index cancels any pending page-advance so the
    // arrow spinner clears and a later-arriving page doesn't yank the user
    // away from where they navigated.
    setPendingAdvance(false);
    setIdx(next);
    setZoom(100);
    setRotation(0);
    setPanOffset({ x: 0, y: 0 });
    setRenaming(false);
  };

  // Advance onto the first newly loaded document once the pending page lands.
  useEffect(() => {
    if (!pendingAdvance) return;
    if (idx < previewDocuments.length - 1) {
      setPendingAdvance(false);
      navigateTo(idx + 1);
    } else if (!canLoadMore) {
      // No more pages after all (e.g. trailing duplicates were deduped away).
      setPendingAdvance(false);
    }
  }, [pendingAdvance, previewDocuments.length, canLoadMore]);

  // Unstick: if the load settles (loadingMore back to false) without the list
  // growing — e.g. fetchMore rejected on a GraphQL error — drop the spinner
  // and re-arm the request guard so the next press retries instead of hanging
  // forever. The timer is cancelled whenever the deps change (new page landed
  // or a fresh load started).
  useEffect(() => {
    if (!pendingAdvance || loadingMore) return;
    const timer = setTimeout(() => {
      lastRequestedLenRef.current = -1;
      setPendingAdvance(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, [pendingAdvance, loadingMore, previewDocuments.length]);

  // Prefetch the next page before the user hits the end of the loaded set so
  // the arrow press is instant in the common case (TA-9771).
  const PREFETCH_AHEAD = 5;
  useEffect(() => {
    if (!canLoadMore || loadingMore) return;
    if (previewDocuments.length - 1 - idx <= PREFETCH_AHEAD) {
      requestMore();
    }
  }, [idx, previewDocuments.length, canLoadMore, loadingMore]);

  // Warm the browser cache for the neighbouring full-res images so prev/next
  // renders instantly instead of flashing while the new image downloads.
  useEffect(() => {
    // DOM Image constructor — absent in the Jest/node environment.
    const ImgCtor = (globalThis as any).Image;
    if (Platform.OS !== 'web' || typeof ImgCtor !== 'function') return;
    [idx - 1, idx + 1, idx + 2].forEach((i) => {
      const neighbour = previewDocuments[i];
      if (!neighbour) return;
      const isImg =
        neighbour.isImage || neighbour.contentType?.includes('image/');
      if (!isImg) return;
      const uri = resolveUri(neighbour);
      if (uri) new ImgCtor().src = uri;
    });
  }, [idx, previewDocuments]);

  // ── View transforms ────────────────────────────────────────────────────────
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [panOffset, setPanOffset] = useState<PanOffset>({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [imageAreaWidth, setImageAreaWidth] = useState<number | undefined>(
    undefined
  );
  const [imageAreaHeight, setImageAreaHeight] = useState<number | undefined>(
    undefined
  );
  const panGestureRef = useRef({
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });
  // Pan gating must be synchronous: on a fast drag the first pointermove can
  // fire before the setIsPanning(true) commit from pointerdown, so gating on
  // the isPanning state would drop the initial delta. The state copy only
  // drives UI (cursor, transition).
  const isPanningRef = useRef(false);
  useEffect(() => {
    // Also end any in-flight pan: navigating mid-drag would otherwise leave
    // isPanning stuck true (no pointerup ever fires for the old gesture),
    // freezing the transform transition at 'none'.
    isPanningRef.current = false;
    setIsPanning(false);
    setZoom(100);
    setRotation(0);
    setPanOffset({ x: 0, y: 0 });
  }, [idx]);

  const clampPanOffset = (
    next: PanOffset,
    nextZoom = zoom,
    width = imageAreaWidth,
    height = imageAreaHeight
  ): PanOffset => {
    const scale = nextZoom / 100;
    if (scale <= 1 || !width || !height) return { x: 0, y: 0 };
    // The image element fills the area (100% × 100%), so a 90°/270° rotation
    // swaps its on-screen box. The pan bound on each axis is the scaled box's
    // overflow beyond the area on that side (0 when the rotated box still
    // fits, so the image can't be dragged away from centre).
    const quarterTurn = rotation % 180 !== 0;
    const boxWidth = quarterTurn ? height : width;
    const boxHeight = quarterTurn ? width : height;
    const maxX = Math.max(0, (boxWidth * scale - width) / 2);
    const maxY = Math.max(0, (boxHeight * scale - height) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, next.x)),
      y: Math.max(-maxY, Math.min(maxY, next.y)),
    };
  };

  // Re-clamp whenever zoom or rotation changes the pan bounds so a stale
  // offset can't leave the image stuck off-centre (clampPanOffset returns
  // {0, 0} at ≤100%, which also covers the reset-on-zoom-out case).
  useEffect(() => {
    setPanOffset((current) => {
      const next = clampPanOffset(current);
      // Preserve the reference when unchanged so React bails out of the
      // re-render on every zoom/rotate step with an in-bounds offset.
      return next.x === current.x && next.y === current.y ? current : next;
    });
  }, [zoom, rotation]);

  // ── Rename state ───────────────────────────────────────────────────────────
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  // Local override so the header reflects the new name immediately after
  // renameDocument succeeds — Apollo cache update is async and won't refresh
  // previewDocuments synchronously.
  const [filenameOverrides, setFilenameOverrides] = useState<
    Record<string, string>
  >({});
  const renameInputRef = useRef<TextInput>(null);
  useEffect(() => {
    if (!renaming) return;
    const id = setTimeout(() => renameInputRef.current?.focus(), 50);
    return () => clearTimeout(id);
  }, [renaming]);

  // ── Selection state ────────────────────────────────────────────────────────
  const [selMode, setSelMode] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  useEffect(() => {
    if (selMode && selected.size === 0) setSelMode(false);
  }, [selected.size, selMode]);
  const [hoveredThumb, setHoveredThumb] = useState<number | null>(null);
  const thumbScrollRef = useRef<ScrollView>(null);
  // Keep the active thumbnail in view when the ACTIVE IMAGE changes (or when
  // the list first populates) — but not when pagination appends a page, which
  // would yank the strip back while the user is scrolling it (TA-9771).
  const stripHasContent = previewDocuments.length > 0;
  useEffect(() => {
    if (!stripHasContent) return;
    const id = setTimeout(() => {
      thumbScrollRef.current?.scrollTo({
        x: idx * (THUMB_W + theme.spacing.xs),
        animated: true,
      });
    }, 50);
    return () => clearTimeout(id);
  }, [idx, stripHasContent]);

  // ── Dropdown visibility ────────────────────────────────────────────────────
  const [moreVisible, setMoreVisible] = useState(false);
  const [hashVisible, setHashVisible] = useState(false);
  const [fwdVisible, setFwdVisible] = useState(false);

  // anchor refs for dropdown positioning
  const hashBtnRef = useRef<any>(null);
  const fwdBtnRef = useRef<any>(null);
  const [hashMenuRight, setHashMenuRight] = useState(4);
  const [fwdMenuRight, setFwdMenuRight] = useState(4);
  const [fwdMenuTop, setFwdMenuTop] = useState<number | undefined>(56);
  const [fwdMenuBottom, setFwdMenuBottom] = useState<number | undefined>(
    undefined
  );
  const fwdFloatingRef = useRef<any>(null);

  // ── Hash (link-to-project/task) state ─────────────────────────────────────
  const [hashSearch, setHashSearch] = useState('');
  const [hashSelected, setHashSelected] = useState<Set<string>>(new Set()); // item IDs
  const [hashInitialSel, setHashInitialSel] = useState<Set<string>>(new Set());
  // In-flight guard for the Link button. The ref blocks same-tick double
  // clicks synchronously (a state flag alone is stale inside the closure);
  // the state drives the spinner/disabled UI.
  const linkingRef = useRef(false);
  const [linking, setLinking] = useState(false);
  // Linked pills: keyed by Document id (not array index) so per-image delete or
  // any reordering of previewDocuments doesn't shift pills onto the wrong doc.
  const [linkedItemsMap, setLinkedItemsMap] = useState<
    Record<string, LinkedItem[]>
  >({});
  const currentDocId = previewDocuments[idx]?.id ?? '';
  const linkedItems = linkedItemsMap[currentDocId] ?? [];

  // Pill hover tooltip (for truncated names >20 chars)
  const pillRefs = useRef<Record<string, any>>({});
  const [hoveredPill, setHoveredPill] = useState<string | null>(null);
  const [pillTooltipPos, setPillTooltipPos] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // Initialize linked pills from existing document associations when doc changes.
  //
  // Per-document associations (projectDocuments/taskDocuments) are AUTHORITATIVE
  // and take priority: each attachment shows only its OWN tags, so linking one
  // image never leaks its pills onto siblings in the same message (TA-9683). The
  // chat attachment fragment now fetches taskDocuments per document.
  //
  // Message-level tagsCollections (built upstream from the message's
  // tagsByAuthorCollections) is used ONLY as a fallback for documents that carry
  // no per-document fields at all — never to override per-document state.
  useEffect(() => {
    const doc = previewDocuments[idx];
    if (!doc) return;
    setLinkedItemsMap((prev) => {
      if (prev[doc.id]) return prev; // already initialized for this doc
      const items: LinkedItem[] = [];
      const seenProjects = new Set<string>();
      const seenTasks = new Set<string>();

      const pushProject = (id?: string, name?: string | null) => {
        if (id && name && !seenProjects.has(id)) {
          seenProjects.add(id);
          items.push({ id, name, type: 'project' });
        }
      };
      const pushTask = (
        id?: string,
        name?: string | null,
        projectId?: string
      ) => {
        if (id && name && !seenTasks.has(id)) {
          seenTasks.add(id);
          items.push({ id, name, type: 'task', projectId });
        }
      };

      const hasPerDocTags =
        doc.projectDocuments !== undefined || doc.taskDocuments !== undefined;

      if (hasPerDocTags) {
        // Per-document associations — this document's own tags only.
        doc.projectDocuments?.forEach((pd) =>
          pushProject(pd?.project?.id, pd?.project?.name)
        );
        doc.taskDocuments?.forEach((td) => {
          const task = td?.task;
          pushTask(task?.id, task?.name, task?.projectId ?? task?.project?.id);
          // Surface the parent project as its own pill (matches handleLink,
          // which always links the implied parent project of a tagged task).
          pushProject(task?.project?.id, task?.project?.name);
        });
      } else {
        // Fallback: message-level tag collections (legacy docs without per-doc
        // fields). Not used once the attachment fragment provides taskDocuments.
        tagsCollections.flat().forEach((tc) => {
          const project = (tc as any)?.project;
          const tasks = (tc as any)?.tasks ?? [];
          pushProject(project?.id, project?.name);
          tasks.forEach((task: any) =>
            pushTask(task?.id, task?.name, task?.projectId ?? project?.id)
          );
        });
      }

      return { ...prev, [doc.id]: items };
    });
  }, [idx, previewDocuments, tagsCollections]);

  // ── Copy link state ────────────────────────────────────────────────────────
  const [linkCopied, setLinkCopied] = useState(false);
  // BottomToast portals straight to document.body instead of relying on a
  // useToast() call — see its own comment for why (TA-10845): this
  // component's local <ToastProvider> below only fixes the RN <Modal>
  // portal case, not react-navigation's transformed modalStyle screens.
  const copyFailedToastRef = useRef<BottomToastHandle>(null);
  const [dlBtnWidth, setDlBtnWidth] = useState<number | undefined>(undefined);

  // ── Modals ─────────────────────────────────────────────────────────────────
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [infoVisible, setInfoVisible] = useState(false);
  const [imgDimensions, setImgDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [imgSize, setImgSize] = useState<string>(() =>
    t('chat.imageViewer.infoDash')
  );
  // Modified timestamp from the BE-only lazy query. Kept separate from doc
  // so the row can be hidden cleanly when the query fails or returns nothing.
  const [imgModifiedAt, setImgModifiedAt] = useState<string | null>(null);

  // ── Data / queries ─────────────────────────────────────────────────────────
  const { data: recentData } = useListRecentProjectsTasksQuery({
    variables: { chatId },
    skip: !hashVisible || !chatId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: allProjectsData } = useListProjectsTasksQuery({
    variables: { first: 100 },
    skip: !hashVisible,
    fetchPolicy: 'cache-and-network',
  });

  // ── Mutations / hooks ──────────────────────────────────────────────────────
  const { downloadFiles, isDownloading, downloadError, clearDownloadError } =
    useDownloadFiles();
  const { renameDocument, loading: renameLoading } = useRenameDocument();
  const { resolveDocumentUrl } = useResolveDocumentUrl();
  const { deleteTaskFileById } = useDeleteTaskFiles();
  const { deleteProjectFileById } = useDeleteProjectDocument();
  const { deleteMessageById } = useDeleteMessage();
  const [deleteMessageDocument] = useDeleteMessageDocumentMutation({
    refetchQueries: ['listMessages'],
  });
  const [updateProjectFileTags] = useUpdateProjectFileTagsMutation();
  const [updateDocumentTags] = useUpdateDocumentTagsMutation();
  // Viewer-only lazy query for BE-provided image metadata (width/height/
  // updatedAt). Kept out of the shared DocumentDetail fragment so a FE-first
  // deploy can't break chat-list/profile/files queries — if this single query
  // fails against an older BE, the info modal silently falls back to
  // Image.getSize for dimensions and hides the Modified row.
  const [fetchImageMeta] = useDocumentsByClientIdsForImageViewerLazyQuery({
    fetchPolicy: 'network-only',
  });

  // ── Current doc ────────────────────────────────────────────────────────────
  const doc = previewDocuments[idx];
  const isActiveVideo = !!(doc?.isVideo || doc?.contentType?.includes('video'));
  const isActiveImage =
    !isActiveVideo && !!(doc?.isImage || doc?.contentType?.includes('image/'));
  const filename = doc ? (filenameOverrides[doc.id] ?? getFilename(doc)) : '';
  const imageUri = doc ? resolveUri(doc) : '';
  // For video, never fall back to the thumbnail URL (which is an image, not a
  // video). If no direct URL is available, pass '' so CachedVideo fetches a
  // fresh URL via clientId instead of trying to play a JPEG as a video. The
  // ImageKit CDN path is only a last resort for a doc with no clientId: it
  // can't be played on networks that block ik.imagekit.io, and CachedVideo
  // skips its S3 lookup whenever it's handed a URL (TA-11066).
  const videoUri = doc
    ? (doc.file?.url ??
      (!doc.clientId && doc.file?.cdnBaseUrl && doc.file?.path
        ? `${doc.file.cdnBaseUrl}${doc.file.path}`
        : ''))
    : '';

  // Project Files docs carry no file.url, only the ImageKit CDN path, which
  // fails on networks that block ik.imagekit.io (TA-11066). Resolve a PDF's
  // S3 URL as soon as it's shown, so "Open in new tab" can still open the
  // real URL synchronously inside the click — opening after an await, or
  // opening a blank tab and navigating it later, gets blocked as a popup.
  const isActivePdf = !!doc?.contentType?.includes('pdf');
  const needsPdfUrlLookup = isActivePdf && !!doc && !doc.file?.url;
  const [resolvedPdfUrl, setResolvedPdfUrl] = useState<{
    docId: string;
    url: string | null;
  } | null>(null);
  useEffect(() => {
    if (!needsPdfUrlLookup || !doc) return;
    let isCancelled = false;
    const docId = doc.id;
    resolveDocumentUrl(doc)
      .catch(() => null)
      .then((url) => {
        if (!isCancelled) setResolvedPdfUrl({ docId, url });
      });
    return () => {
      isCancelled = true;
    };
    // Keyed on the doc's id: `resolveDocumentUrl` is recreated every render
    // and `doc` is re-read from context, so either would re-run the lookup.
  }, [doc?.id, needsPdfUrlLookup]);
  const resolvedPdfUrlForDoc =
    resolvedPdfUrl?.docId === doc?.id ? resolvedPdfUrl : null;
  const isResolvingPdfUrl = needsPdfUrlLookup && !resolvedPdfUrlForDoc;
  // The CDN path stays only as a last resort when the S3 lookup fails.
  const pdfOpenUrl = doc?.file?.url || resolvedPdfUrlForDoc?.url || imageUri;

  const isSelfMessage = !!me?.id && doc?.owner?.id === me.id;
  const senderName = isSelfMessage
    ? t('chat.imageViewer.you')
    : (doc?.owner?.name ?? '');
  const dateLabel = doc?.createdAt
    ? t('chat.imageViewer.dateAtTime', {
        date: formatTime('timeAgoMessageList', { val: doc.createdAt }),
        time: dayjs(doc.createdAt).format('hh:mm A'),
      })
    : '';

  const panHandlers =
    Platform.OS === 'web' && isActiveImage
      ? ({
          onPointerDown: (e: any) => {
            if (zoom <= 100 || e.button !== 0) return;
            // Only the image itself is a pan surface. The area also contains
            // overlaid controls (prev/next arrows, selection bar) — starting a
            // pan there would preventDefault + capture the pointer and swallow
            // the nested Pressable's click sequence, making navigation
            // unclickable while zoomed (TA-10281). closest() is absent on the
            // synthetic events jsdom tests dispatch; those keep the pan path.
            const target = e.target;
            if (
              typeof target?.closest === 'function' &&
              target !== e.currentTarget &&
              !target.closest('[data-testid="media-viewer-main-image"]')
            ) {
              return;
            }
            e.preventDefault?.();
            e.stopPropagation?.();
            // TA-10848 (FRONTEND-2Z6): setPointerCapture throws NotFoundError
            // / InvalidStateError when the pointer is no longer active (e.g.
            // lifted before this handler ran). Capture is best-effort, so the
            // pan still starts without it, mirroring the release guard below.
            try {
              e.currentTarget?.setPointerCapture?.(e.pointerId);
            } catch (error: unknown) {
              // Pan without capture; anything else is a real bug.
              if (!isInactivePointerCaptureError(error)) throw error;
            }
            panGestureRef.current = {
              startX: e.clientX,
              startY: e.clientY,
              originX: panOffset.x,
              originY: panOffset.y,
            };
            isPanningRef.current = true;
            setIsPanning(true);
          },
          onPointerMove: (e: any) => {
            if (!isPanningRef.current) return;
            e.preventDefault?.();
            const next = clampPanOffset({
              x:
                panGestureRef.current.originX +
                e.clientX -
                panGestureRef.current.startX,
              y:
                panGestureRef.current.originY +
                e.clientY -
                panGestureRef.current.startY,
            });
            setPanOffset(next);
          },
          onPointerUp: (e: any) => {
            // releasePointerCapture can throw NotFoundError for a pointer we
            // never captured (e.g. the down happened at ≤100% zoom).
            if (e.currentTarget?.hasPointerCapture?.(e.pointerId)) {
              e.currentTarget.releasePointerCapture(e.pointerId);
            }
            isPanningRef.current = false;
            setIsPanning(false);
          },
          onPointerCancel: (e: any) => {
            if (e.currentTarget?.hasPointerCapture?.(e.pointerId)) {
              e.currentTarget.releasePointerCapture(e.pointerId);
            }
            isPanningRef.current = false;
            setIsPanning(false);
          },
          onPointerLeave: (e: any) => {
            // With pointer capture held, the drag legitimately continues
            // outside the element bounds — don't end the pan. The reset only
            // applies where capture isn't active (unsupported browsers).
            if (e?.currentTarget?.hasPointerCapture?.(e.pointerId)) return;
            isPanningRef.current = false;
            setIsPanning(false);
          },
        } as any)
      : {};

  // ── Delete branching ──────────────────────────────────────────────────────
  // Four real paths, picked by attachment count + caller context:
  //  1. deleteMessageDocument(id) — new in TA-9709 (tasktag-backend#3905).
  //     Removes one attachment + its joins via dependent: :destroy, leaving
  //     the parent message intact. Used when the message has >1 attachment.
  //  2. deleteMessage — deletes the whole message (all attachments). Used
  //     when only one attachment remains (or as a graceful fallback when #1
  //     is not yet available on the deployed BE).
  //  3. deleteTaskFile / deleteProjectFile — only unlinks the tag, doesn't
  //     remove the image. Used by the task/project file panel callers.
  //  4. (legacy) the confirm dialog warns when the action would remove more
  //     than the visible image.
  const taskDocs = doc?.taskDocuments ?? [];
  const projectDocs = doc?.projectDocuments ?? [];
  const isOwner = !!me?.id && doc?.owner?.id === me.id;
  // Note: a bare `taskId` prop (no resolved task join on the document) is
  // intentionally excluded — deleteTaskFiles expects TaskDocument join IDs,
  // not Task IDs, so we can't safely delete without a real taskDocs entry.
  const canDelete =
    isOwner && (!!messageId || taskDocs.length > 0 || projectDocs.length > 0);

  // ── Scroll wheel zoom ──────────────────────────────────────────────────────
  const imageAreaRef = useRef<any>(null);
  const hashVisibleRef = useRef(hashVisible);
  const fwdVisibleRef = useRef(fwdVisible);
  const moreVisibleRef = useRef(moreVisible);
  const infoVisibleRef = useRef(infoVisible);
  const deleteConfirmRef = useRef(deleteConfirm);
  const isActiveVideoRef = useRef(isActiveVideo);
  const lastWheelRef = useRef<number>(0);
  useEffect(() => {
    hashVisibleRef.current = hashVisible;
  }, [hashVisible]);
  useEffect(() => {
    fwdVisibleRef.current = fwdVisible;
  }, [fwdVisible]);
  useEffect(() => {
    moreVisibleRef.current = moreVisible;
  }, [moreVisible]);
  useEffect(() => {
    infoVisibleRef.current = infoVisible;
  }, [infoVisible]);
  useEffect(() => {
    deleteConfirmRef.current = deleteConfirm;
  }, [deleteConfirm]);
  useEffect(() => {
    isActiveVideoRef.current = isActiveVideo;
  }, [isActiveVideo]);
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const node = imageAreaRef.current;
    if (!node) return;
    // Attach to the image canvas element only so that scroll events inside
    // dropdowns and the rest of the page (e.g. chat tag popup) are unaffected.
    let domNode: HTMLElement | null = null;
    if (typeof node.getScrollableNode === 'function') {
      const scrollable = node.getScrollableNode();
      domNode = scrollable instanceof HTMLElement ? scrollable : null;
    } else if (!node._nativeTag) {
      domNode = node as unknown as HTMLElement;
    }
    const target: HTMLElement | null =
      domNode instanceof HTMLElement ? domNode : null;
    if (!target) return;
    const onWheel = (e: WheelEvent) => {
      if (
        isActiveVideoRef.current ||
        hashVisibleRef.current ||
        fwdVisibleRef.current ||
        moreVisibleRef.current ||
        infoVisibleRef.current ||
        deleteConfirmRef.current
      )
        return;
      e.preventDefault();
      const now = Date.now();
      if (now - lastWheelRef.current < WHEEL_THROTTLE_MS) return;
      lastWheelRef.current = now;
      if (e.deltaY < 0) setZoom((z) => Math.min(z + ZOOM_STEP, ZOOM_MAX));
      else setZoom((z) => Math.max(z - ZOOM_STEP, ZOOM_MIN));
    };
    target.addEventListener('wheel', onWheel, { passive: false });
    return () => target.removeEventListener('wheel', onWheel);
  }, []);

  // ── Dismiss link-copied on outside click ───────────────────────────────────
  const closeLinkCopied = useCallback(() => setLinkCopied(false), []);

  useGlobalPointerListener('mousedown', closeLinkCopied, {
    target: 'window',
    enabled: linkCopied && Platform.OS === 'web',
  });

  // Surface download failures instead of letting them fail silently (TA-10292)
  // — e.g. when more than one file in a multi-select batch can't be fetched
  // and useDownloadFiles reports `files.downloadPartialFailure`.
  useEffect(() => {
    if (!downloadError) return;
    toast.show(
      <CustomToast>
        <Text color='white'>{t(downloadError)}</Text>
      </CustomToast>,
      {
        type: 'custom',
        placement: 'bottom',
        duration: 3000,
        animationType: 'zoom-in',
      }
    );
    clearDownloadError();
  }, [downloadError, clearDownloadError, t, toast]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleDownload = () => {
    // Ignore repeat taps while a download is already in flight.
    if (isDownloading) return;
    if (selMode && selected.size > 0) {
      downloadFiles(Array.from(selected).map((i) => previewDocuments[i]));
    } else if (doc) {
      // Pass the viewer's local doc explicitly. `downloadFile()` reads from
      // context (`previewDocuments[activeIndex]`), which can transiently
      // diverge from this component's `idx` and download the wrong image.
      downloadFiles([doc]);
    }
  };

  const handleOpenPdf = () => {
    // Passing `noopener`/`noreferrer` makes `window.open` return null *by
    // spec*, not only when a popup is blocked. Reading that null as "blocked"
    // ran the download fallback on every click too, which resolves a
    // presigned S3 URL a moment later and opens the same file in a second
    // tab (TA-10973). Open without the feature string so the return value
    // actually means something, then sever `opener` by hand — that keeps the
    // tabnabbing protection `noopener` was there for.
    const win = window.open(pdfOpenUrl, '_blank');
    if (win) {
      win.opener = null;
    } else {
      handleDownload();
    }
  };

  const startRename = () => {
    const { base } = splitExt(filename);
    setRenameValue(base);
    setRenaming(true);
  };

  const commitRename = async () => {
    const trimmed = renameValue.trim();
    if (!trimmed || !doc?.id) {
      setRenaming(false);
      return;
    }
    const { ext } = splitExt(filename);
    const newFullName = trimmed + ext;
    const docId = doc.id;
    await renameDocument(
      docId,
      newFullName,
      () => {
        setFilenameOverrides((prev) => ({ ...prev, [docId]: newFullName }));
        setRefreshList(true);
        toast.show(
          <CustomToast>
            <Text color='white'>{t('chat.imageViewer.fileRenamed')}</Text>
          </CustomToast>,
          {
            type: 'custom',
            placement: 'bottom',
            duration: 2000,
            animationType: 'zoom-in',
          }
        );
      },
      () => {
        toast.show(
          <CustomToast>
            <Text color='white'>{t('chat.imageViewer.renameFailed')}</Text>
          </CustomToast>,
          {
            type: 'custom',
            placement: 'bottom',
            duration: 2000,
            animationType: 'zoom-in',
          }
        );
      }
    );
    setRenaming(false);
  };

  const cancelRename = () => setRenaming(false);

  const handleDelete = async () => {
    if (!doc) return;
    const onDeleteWholeMessageSuccess = () => {
      setDeleteConfirm(false);
      setRefreshList(true);
      onDismiss?.();
    };

    if (messageId) {
      // Multi-image message → remove this single document via the per-doc
      // mutation. The BE destroys the Document and its message/project/task
      // joins via dependent: :destroy, leaving the parent message intact.
      if (previewDocuments.length > 1) {
        try {
          await deleteMessageDocument({ variables: { id: doc.id } });
          setDeleteConfirm(false);
          setRefreshList(true);
          // Remove the deleted doc from the preview context so the viewer
          // doesn't keep showing a stale image at the old index.
          const newDocs = previewDocuments.filter((d) => d.id !== doc.id);
          setPreviewDocuments(newDocs);
          setSelected(new Set());
          setSelMode(false);
          navigateTo(Math.min(idx, newDocs.length - 1));
        } catch {
          setDeleteConfirm(false);
        }
        return;
      }

      // Single image in the message → delete the whole message.
      deleteMessageById(
        { __typename: 'Message', id: messageId } as any,
        onDeleteWholeMessageSuccess
      );
      return;
    }

    // Fallback for non-chat callers (task/project file panels). Requires a
    // real taskDocs/projectDocs entry because the delete mutations take
    // TaskDocument / ProjectDocument JOIN IDs, not the parent Task / Project
    // ID. The bare `taskId` prop alone is not enough — `canDelete` enforces
    // this so the UI button is hidden in that case.
    if (taskDocs.length > 0) {
      deleteTaskFileById(taskDocs as any, chatId, () => {
        setDeleteConfirm(false);
        if (previewDocuments.length <= 1) {
          onDismiss?.();
        } else {
          navigateTo(Math.min(idx, previewDocuments.length - 2));
        }
        setRefreshList(true);
      });
    } else if (projectDocs.length > 0) {
      deleteProjectFileById(projectDocs as any, chatId, () => {
        setDeleteConfirm(false);
        if (previewDocuments.length <= 1) {
          onDismiss?.();
        } else {
          navigateTo(Math.min(idx, previewDocuments.length - 2));
        }
        setRefreshList(true);
      });
    }
  };

  // Single timer ref so consecutive copies / unmounts don't leak setTimeouts.
  const linkCopiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (linkCopiedTimerRef.current) clearTimeout(linkCopiedTimerRef.current);
    },
    []
  );
  const flashLinkCopied = () => {
    setLinkCopied(true);
    if (linkCopiedTimerRef.current) clearTimeout(linkCopiedTimerRef.current);
    linkCopiedTimerRef.current = setTimeout(() => setLinkCopied(false), 3000);
  };

  const showCopyFailedToast = (
    reason: keyof typeof CLIPBOARD_FAILURE_MESSAGE_KEYS = 'unknown'
  ) => {
    copyFailedToastRef.current?.show(
      t(CLIPBOARD_FAILURE_MESSAGE_KEYS[reason]),
      { duration: 3000, icon: 'warning' }
    );
  };

  const handleCopyLink = async () => {
    let docs: Document[];
    if (selMode && selected.size > 0) {
      docs = Array.from(selected)
        .map((i) => previewDocuments[i])
        .filter(Boolean) as Document[];
    } else if (doc) {
      docs = [doc];
    } else {
      return;
    }
    if (docs.length === 0) return;
    if (onCopyLink) {
      const result = await onCopyLink(docs);
      // `undefined` means the caller already showed its own feedback
      // (e.g. a paywall modal) — don't also flash a copy pill.
      if (result === undefined) return;
      if (result.success) {
        flashLinkCopied();
      } else {
        showCopyFailedToast(result.reason);
      }
      return;
    }
    const url = (await onShareImages(docs)) ?? imageUri;
    const result = await copyTextToClipboard(url);
    if (result.success) {
      flashLinkCopied();
    } else {
      showCopyFailedToast(result.reason);
    }
  };

  const copyLinkIconColor = linkCopied
    ? theme.colors.greenSecondary
    : theme.colors.white;

  const handleOpenInfo = () => {
    setMoreVisible(false);
    // Always set the client-side fallback first so the modal renders something
    // even if the BE query is slow or fails.
    if (imageUri) {
      Image.getSize(
        imageUri,
        (w, h) => setImgDimensions({ width: w, height: h }),
        () => setImgDimensions(null)
      );
    }
    setImgSize(
      doc?.size
        ? t('chat.imageViewer.kb', { size: Math.round(doc.size / 1024) })
        : t('chat.imageViewer.infoDash')
    );
    setImgModifiedAt(null);
    setInfoVisible(true);

    // Lazily fetch BE-provided width/height/updatedAt. Kept off the shared
    // DocumentDetail fragment so an FE-first deploy can't break unrelated
    // queries — if this single query fails (older BE), the modal keeps its
    // client-side fallbacks above and the Modified row stays hidden.
    if (!doc?.clientId) return;
    fetchImageMeta({ variables: { clientIds: [doc.clientId] } })
      .then(({ data, error }) => {
        if (error) return;
        const meta = data?.documentsByClientIds?.[0];
        if (!meta) return;
        if (meta.width && meta.height) {
          setImgDimensions({ width: meta.width, height: meta.height });
        }
        if (meta.updatedAt) {
          setImgModifiedAt(meta.updatedAt);
        }
      })
      .catch(() => {
        // BE doesn't have the fields yet — silently keep fallbacks.
      });
  };

  // ── Hash dropdown data ─────────────────────────────────────────────────────
  const buildHashItems = (): { recent: HashItem[]; all: HashItem[] } => {
    const recent: HashItem[] = [];
    (recentData?.listRecentProjectsTasks ?? []).forEach((item) => {
      if (!item) return;
      if (item.__typename === 'Project') {
        recent.push({ id: item.id, name: item.name, type: 'project' });
      } else if (item.__typename === 'Task') {
        recent.push({
          id: item.id,
          name: item.name,
          type: 'task',
          projectId: item.projectId ?? undefined,
        });
      }
    });

    const all: HashItem[] = [];
    (allProjectsData?.listProjectsTasks?.edges ?? []).forEach((edge) => {
      const project = edge?.node;
      if (!project) return;
      all.push({ id: project.id, name: project.name, type: 'project' });
      (project.tasks ?? []).forEach((task) => {
        if (task && !task.deletedAt && !task.completedAt) {
          all.push({
            id: task.id,
            name: task.name,
            type: 'task',
            projectId: task.projectId ?? undefined,
          });
        }
      });
    });
    return { recent, all };
  };

  const { recent: recentItems, all: allItems } = useMemo(
    () => buildHashItems(),
    [recentData, allProjectsData]
  );

  const filterItems = (items: HashItem[]) =>
    !hashSearch
      ? items
      : items.filter((i) =>
          i.name.toLowerCase().includes(hashSearch.toLowerCase())
        );

  const toggleHashItem = (item: HashItem) => {
    setHashSelected((prev) => {
      const next = new Set(prev);
      if (next.has(item.id)) {
        next.delete(item.id);
      } else {
        next.add(item.id);
        // auto-select parent project when a task is selected
        if (item.type === 'task' && item.projectId) {
          next.add(item.projectId);
        }
      }
      return next;
    });
  };

  const handleLink = async () => {
    if (!doc?.id || linkingRef.current) return;
    linkingRef.current = true;
    setLinking(true);
    // Everything below runs inside try/finally so the in-flight lock can never
    // leak — without it, an unexpected throw (even from the rollback path)
    // would leave the Link button disabled for the rest of the viewer session.
    try {
      const allHashItems = [...recentItems, ...allItems];
      // O(n) lookup map — avoids repeated find() inside forEach loops.
      const itemsById = new Map(allHashItems.map((i) => [i.id, i]));

      // Expand the selection to include any parent project implied by a selected
      // task, so the project pill renders alongside its task.
      const impliedProjectIds = new Set<string>();
      hashSelected.forEach((id) => {
        const found = itemsById.get(id);
        if (found?.type === 'task' && found.projectId)
          impliedProjectIds.add(found.projectId);
      });
      const effectiveIds = new Set([...hashSelected, ...impliedProjectIds]);

      const projectIds = Array.from(effectiveIds)
        .map((id) => itemsById.get(id))
        .filter((i): i is HashItem => i?.type === 'project')
        .map((i) => i.id);
      const taskIds = Array.from(effectiveIds)
        .map((id) => itemsById.get(id))
        .filter((i): i is HashItem => i?.type === 'task')
        .map((i) => i.id);

      // Wrapped in try/catch — Apollo mutations can reject the awaited promise.
      const docId = doc.id;
      const previousLinkedItems = linkedItems;
      try {
        if (messageId) {
          // Chat context: a single per-document mutation links THIS document only,
          // recomputes the message-level tag union (so the chat bubble still shows
          // every image's tags) and fires cross-surface subscriptions. Avoids the
          // updateMessageTags whole-message fan-out that contaminated siblings.
          await updateDocumentTags({
            variables: {
              id: docId,
              messageId,
              attributes: { projectIds, taskIds },
            },
          });
        } else {
          // Files/Media context (document not tied to a chat message): keep the
          // per-project file-tag mutation. taskIds:[] removes the document from a
          // project the user deselected.
          const taskMap: Record<string, string[]> = {}; // projectId -> taskIds[]
          hashSelected.forEach((id) => {
            const found = itemsById.get(id);
            if (found?.type === 'task' && found.projectId) {
              if (!taskMap[found.projectId]) taskMap[found.projectId] = [];
              taskMap[found.projectId].push(found.id);
            }
          });
          hashSelected.forEach((id) => {
            const found = itemsById.get(id);
            if (found?.type === 'project' && !taskMap[found.id]) {
              taskMap[found.id] = [];
            }
          });

          const previousProjectIds = new Set<string>();
          linkedItems.forEach((item) => {
            if (item.type === 'project') previousProjectIds.add(item.id);
            else if (item.type === 'task' && item.projectId)
              previousProjectIds.add(item.projectId);
          });

          for (const [projectId, ids] of Object.entries(taskMap)) {
            await updateProjectFileTags({
              variables: { id: docId, projectId, taskIds: ids },
            });
          }
          for (const projectId of previousProjectIds) {
            if (!taskMap[projectId]) {
              await updateProjectFileTags({
                variables: { id: docId, projectId, taskIds: [] },
              });
            }
          }
        }

        const newItems = Array.from(effectiveIds)
          .map((selId) => itemsById.get(selId))
          .filter(Boolean) as HashItem[];
        setLinkedItemsMap((prev) => ({ ...prev, [docId]: newItems }));
        setRefreshList(true);
      } catch {
        // Restore previous pills so the UI doesn't drift from the server.
        setLinkedItemsMap((prev) => ({
          ...prev,
          [docId]: previousLinkedItems,
        }));
      }
    } finally {
      linkingRef.current = false;
      setLinking(false);
      setHashVisible(false);
      setHashSelected(new Set());
      setHashSearch('');
    }
  };

  const toggleSelect = (i: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  const selCount = selected.size;
  const hasPills = linkedItems.length > 0;

  if (!doc) return null;

  const renderMediaContent = () => {
    if (isActiveVideo) {
      return (
        <CachedVideo
          key={doc.id}
          clientId={doc.clientId}
          url={videoUri}
          thumbnail={doc.file?.thumbnail ?? undefined}
          screenWidth={imageAreaWidth}
          screenHeight={imageAreaHeight}
        />
      );
    }
    if (isActiveImage) {
      return (
        // CachedImage resolves the S3 URL by clientId, the same way the task
        // panel's ImagesPreview does — so the viewer still renders on networks
        // that block or SSL-intercept the ImageKit CDN instead of going black.
        // Keyed on doc.id so its resolved-URL state can't flash the previous
        // image while the next one resolves.
        <CachedImage
          key={doc.id}
          testID='media-viewer-main-image'
          imageClientId={doc.clientId ?? undefined}
          image={doc.file}
          fullResolution
          screenWidth={imageAreaWidth}
          height={imageAreaHeight}
          style={{
            // Order matters: earlier entries are the OUTER transforms.
            // Translate must come before scale/rotate so the pan offset is
            // applied in screen pixels, unaffected by zoom/rotation —
            // clampPanOffset's bounds math depends on this. Do not reorder.
            transform: [
              { translateX: panOffset.x },
              { translateY: panOffset.y },
              { scale: zoom / 100 },
              { rotate: `${rotation}deg` },
            ],
            backgroundColor: theme.colors.transparent,
            ...(Platform.OS === 'web'
              ? ({
                  transition: isPanning ? 'none' : 'transform 0.18s ease-out',
                } as any)
              : {}),
          }}
          resizeMode='contain'
        />
      );
    }
    return (
      <Box style={s.filePreviewBox}>
        <Icon
          fileType={doc.contentType || undefined}
          color='blue'
          variant='xl'
          width={120}
          height={120}
        />
        {isActivePdf && imageUri ? (
          <Button
            width={200}
            height={48}
            isSmall
            backgroundColor='grey01'
            isLoading={isResolvingPdfUrl}
            onPress={handleOpenPdf}
            borderRadius='xs'
            variant='reversed'
            color='black'
            accessibilityLabel={t('chat.imageViewer.openInNewTab')}>
            {t('chat.imageViewer.openInNewTab')}
          </Button>
        ) : (
          <Button
            width={200}
            height={48}
            isSmall
            backgroundColor='grey01'
            prefix={
              <Images.Download
                width={16}
                height={16}
                color={theme.colors.black}
              />
            }
            onPress={handleDownload}
            borderRadius='xs'
            variant='reversed'
            color='black'
            prefixMarginRight='xs'
            accessibilityLabel={t('chat.imageViewer.download')}>
            {t('chat.imageViewer.download')}
          </Button>
        )}
      </Box>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Modal visible transparent animationType='fade' onRequestClose={onDismiss}>
      {/* TA-10299: this viewer renders through RN's <Modal>, which on web
          portals its content into a fresh div appended directly to
          document.body — a completely separate DOM subtree from #root, where
          the app-level ToastProvider's toast container lives. Every
          react-native-web View defaults to zIndex: 0 (which DOES establish a
          stacking context), so the outer toast's zIndex: 999999 is trapped
          inside #root and can never out-stack this modal's content. Nesting a
          local ToastProvider here — wrapping s.root rather than nested
          deeper inside any single child box — gives descendants' useToast()
          calls (e.g. ChatImageForwardPicker's) a toast container that lives
          in this SAME portaled subtree, so it stacks correctly above the
          viewer instead of invisibly behind it. It must sit here, not nested
          inside the fwdMenu dropdown box specifically — that box has
          overflow: 'hidden' at a fixed 420x520 size, which clips a toast
          nested inside it even though the stacking context is otherwise
          correct. Note: MediaViewer's OWN useToast() call above (used for
          rename toasts) is evaluated at this component's own position in the
          tree, not inside what it returns here — so this fix does not
          extend to those; any other top-level `toast.show()` call in this
          file has the same invisible-behind-the-modal bug. The copy-link
          failure message below uses BottomToast instead (TA-10845) — a
          nested ToastProvider only fixes THIS RN <Modal>'s own portal, but
          the same copy-link button is also reachable through a
          react-navigation `modalStyle` screen (see navigation/helpers.tsx's
          cardStyleInterpolator) whose `translateY` transform traps a
          "position: fixed" toast the exact same way, no matter how deep the
          local ToastProvider sits. BottomToast portals straight to
          document.body, sidestepping both. Also patched
          (patches/react-native-toast-notifications+
          3.4.0.patch): the library's ToastProvider set a module-level
          GlobalToast ref on mount and never reset it on unmount, so every
          time this viewer opened/closed it would permanently re-point the
          app-wide `Toast.show()` global (used elsewhere, e.g.
          ProjectFormStack) at this viewer's own dead container. The patch
          restores the previous GlobalToast on unmount. */}
      <ToastProvider>
        <BottomToast ref={copyFailedToastRef} />
        <Box style={s.root}>
          {/* ── HEADER ────────────────────────────────────────────────────────── */}
          <Box style={s.header}>
            {/* LEFT: back · avatar · name · date */}
            <Box style={s.hLeft}>
              <Pressable
                onPress={onDismiss}
                style={s.iconBtn}
                hitSlop={10}
                accessibilityLabel={t('chat.imageViewer.closeViewer')}>
                <Images.ChevronLeft
                  width={24}
                  height={24}
                  color={theme.colors.white}
                />
              </Pressable>
              <Box ml='xxs' flexShrink={0}>
                <Avatar
                  id={doc.owner?.id ?? ''}
                  label={doc.owner?.name ?? senderName}
                  avatar={doc.owner?.avatar ?? null}
                  size='small'
                />
              </Box>
              <Box style={s.senderBlock}>
                <Text style={s.senderName} numberOfLines={1}>
                  {senderName}
                </Text>
                <Text style={s.dateLabel} numberOfLines={1}>
                  {dateLabel}
                </Text>
              </Box>
            </Box>

            {/* CENTER: filename / rename */}
            <Box style={s.hCenter}>
              {renaming ? (
                <Box style={s.renameRow}>
                  <TextInput
                    ref={renameInputRef}
                    style={s.renameInput}
                    value={renameValue}
                    onChangeText={setRenameValue}
                    onSubmitEditing={commitRename}
                    selectTextOnFocus
                    autoFocus
                    editable={!renameLoading}
                  />
                  <ToolbarTooltip label={t('chat.imageViewer.cancel')}>
                    <Pressable
                      onPress={cancelRename}
                      style={({ pressed, hovered }: any) => [
                        s.iconBtn24,
                        (pressed || hovered) && s.iconBtnHover,
                      ]}
                      hitSlop={8}
                      accessibilityLabel={t('chat.imageViewer.cancelRename')}>
                      <Images.X
                        width={16}
                        height={16}
                        color={theme.colors.alertRed}
                      />
                    </Pressable>
                  </ToolbarTooltip>
                  <ToolbarTooltip label={t('chat.imageViewer.save')}>
                    <Pressable
                      onPress={commitRename}
                      style={({ pressed, hovered }: any) => [
                        s.iconBtn24,
                        (pressed || hovered) && s.iconBtnHover,
                      ]}
                      hitSlop={8}
                      accessibilityLabel={t('chat.imageViewer.saveRename')}>
                      <Images.Check
                        width={16}
                        height={16}
                        color={theme.colors.greenSecondary}
                      />
                    </Pressable>
                  </ToolbarTooltip>
                </Box>
              ) : (
                <Box style={s.filenameRow}>
                  <Text style={s.filename} numberOfLines={1}>
                    {filename}
                  </Text>
                  {isOwner && !isViewerOnly && (
                    <ToolbarTooltip label={t('chat.imageViewer.rename')}>
                      <Pressable
                        onPress={startRename}
                        style={({ pressed, hovered }: any) => [
                          s.iconBtn24,
                          (pressed || hovered) && s.iconBtnHover,
                        ]}
                        hitSlop={8}
                        accessibilityLabel={t('chat.imageViewer.renameFile')}>
                        <Images.Edit3
                          width={14}
                          height={14}
                          color={theme.colors.white}
                        />
                      </Pressable>
                    </ToolbarTooltip>
                  )}
                </Box>
              )}
            </Box>

            {/* RIGHT: toolbar groups */}
            <Box style={s.hRight}>
              {/* Group 1: link (chat only) · download · forward · copy */}
              <Box style={s.iconGroup}>
                {!!chatId && (
                  <ToolbarTooltip
                    label={t('chat.imageViewer.linkToProjectOrTask')}>
                    <Pressable
                      ref={hashBtnRef}
                      onPress={() => {
                        const toggleHash = () => {
                          setHashVisible((v) => {
                            if (!v) {
                              const initial = new Set(
                                linkedItems.map((i) => i.id)
                              );
                              setHashSelected(initial);
                              setHashInitialSel(new Set(initial));
                            }
                            return !v;
                          });
                        };
                        setHashSearch('');
                        if (hashBtnRef.current && Platform.OS === 'web') {
                          hashBtnRef.current.measure(
                            (
                              _x: number,
                              _y: number,
                              width: number,
                              _h: number,
                              pageX: number
                            ) => {
                              setHashMenuRight(
                                (window as any).innerWidth - pageX - width
                              );
                              toggleHash();
                            }
                          );
                        } else {
                          toggleHash();
                        }
                      }}
                      style={({ pressed, hovered }: any) => [
                        s.iconBtn,
                        (pressed || hovered) && s.iconBtnHover,
                        hashVisible && s.iconBtnHover,
                      ]}
                      hitSlop={10}
                      accessibilityLabel={t(
                        'chat.imageViewer.linkToProjectOrTask'
                      )}>
                      <Images.Hash
                        width={TOOLBAR_ICON}
                        height={TOOLBAR_ICON}
                        color={
                          hashVisible
                            ? theme.colors.greenSecondary
                            : theme.colors.white
                        }
                      />
                    </Pressable>
                  </ToolbarTooltip>
                )}
                {!isViewerOnly && !selMode && (
                  <>
                    <ToolbarTooltip label={t('chat.imageViewer.download')}>
                      <Pressable
                        onPress={handleDownload}
                        style={({ pressed, hovered }: any) => [
                          s.iconBtn,
                          (pressed || hovered) && s.iconBtnHover,
                        ]}
                        hitSlop={10}
                        accessibilityLabel={t('chat.imageViewer.download')}>
                        <Images.Download
                          width={TOOLBAR_ICON}
                          height={TOOLBAR_ICON}
                          color={theme.colors.white}
                        />
                      </Pressable>
                    </ToolbarTooltip>
                    <ToolbarTooltip label={t('chat.imageViewer.forward')}>
                      <Pressable
                        ref={fwdBtnRef}
                        onPress={() => {
                          if (fwdBtnRef.current && Platform.OS === 'web') {
                            fwdBtnRef.current.measure(
                              (
                                _x: number,
                                _y: number,
                                width: number,
                                _h: number,
                                pageX: number
                              ) => {
                                setFwdMenuRight(
                                  (window as any).innerWidth - pageX - width
                                );
                                setFwdMenuTop(56);
                                setFwdMenuBottom(undefined);
                                setFwdVisible((v) => !v);
                              }
                            );
                          } else {
                            setFwdMenuTop(56);
                            setFwdMenuBottom(undefined);
                            setFwdVisible((v) => !v);
                          }
                        }}
                        style={({ pressed, hovered }: any) => [
                          s.iconBtn,
                          (pressed || hovered) && s.iconBtnHover,
                          fwdVisible && s.iconBtnHover,
                        ]}
                        hitSlop={10}
                        accessibilityLabel={t('chat.imageViewer.forward')}>
                        <Images.ForwardStroke
                          width={TOOLBAR_ICON}
                          height={TOOLBAR_ICON}
                          color={
                            fwdVisible
                              ? theme.colors.greenSecondary
                              : theme.colors.white
                          }
                        />
                      </Pressable>
                    </ToolbarTooltip>
                  </>
                )}
                {!isViewerOnly && (
                  <ToolbarTooltip
                    label={t('chat.imageViewer.copyLinkToShare')}
                    forceOpen={linkCopied}
                    customContent={
                      linkCopied ? (
                        <>
                          <Images.CheckCircle
                            width={14}
                            height={14}
                            color={theme.colors.greenSecondary}
                          />
                          <Text style={s.linkCopiedTxt}>
                            {t('chat.imageViewer.linkCopied')}
                          </Text>
                        </>
                      ) : undefined
                    }>
                    <Pressable
                      onPress={handleCopyLink}
                      style={({ pressed, hovered }: any) => [
                        s.iconBtn,
                        (pressed || hovered) && s.iconBtnHover,
                      ]}
                      hitSlop={10}
                      accessibilityLabel={t('chat.imageViewer.copyLink')}>
                      <Images.Link
                        width={TOOLBAR_ICON}
                        height={TOOLBAR_ICON}
                        color={copyLinkIconColor}
                      />
                    </Pressable>
                  </ToolbarTooltip>
                )}
              </Box>

              {isActiveImage && (
                <>
                  <Box style={s.groupDivider} />

                  {/* Group 2: rotate · zoom out · % · zoom in */}
                  <Box style={s.iconGroup}>
                    <ToolbarTooltip label={t('chat.imageViewer.rotate')}>
                      <Pressable
                        onPress={() => setRotation((r) => (r + 90) % 360)}
                        style={({ pressed, hovered }: any) => [
                          s.iconBtn,
                          (pressed || hovered) && s.iconBtnHover,
                        ]}
                        hitSlop={10}
                        accessibilityLabel={t('chat.imageViewer.rotate')}>
                        <Images.RotateCw
                          width={TOOLBAR_ICON}
                          height={TOOLBAR_ICON}
                          color={theme.colors.white}
                        />
                      </Pressable>
                    </ToolbarTooltip>
                    <ToolbarTooltip label={t('chat.imageViewer.zoomOut')}>
                      <Pressable
                        onPress={() =>
                          setZoom((z) => Math.max(z - ZOOM_STEP, ZOOM_MIN))
                        }
                        style={({ pressed, hovered }: any) => [
                          s.iconBtn,
                          (pressed || hovered) && s.iconBtnHover,
                        ]}
                        hitSlop={10}
                        accessibilityLabel={t('chat.imageViewer.zoomOut')}>
                        <Images.ZoomOut
                          width={TOOLBAR_ICON}
                          height={TOOLBAR_ICON}
                          color={
                            zoom <= ZOOM_MIN
                              ? theme.colors.scrimLight30
                              : theme.colors.white
                          }
                        />
                      </Pressable>
                    </ToolbarTooltip>
                    <Text style={s.zoomPct}>{zoom}%</Text>
                    <ToolbarTooltip label={t('chat.imageViewer.zoomIn')}>
                      <Pressable
                        onPress={() =>
                          setZoom((z) => Math.min(z + ZOOM_STEP, ZOOM_MAX))
                        }
                        style={({ pressed, hovered }: any) => [
                          s.iconBtn,
                          (pressed || hovered) && s.iconBtnHover,
                        ]}
                        hitSlop={10}
                        accessibilityLabel={t('chat.imageViewer.zoomIn')}>
                        <Images.ZoomIn
                          width={TOOLBAR_ICON}
                          height={TOOLBAR_ICON}
                          color={
                            zoom >= ZOOM_MAX
                              ? theme.colors.scrimLight30
                              : theme.colors.white
                          }
                        />
                      </Pressable>
                    </ToolbarTooltip>
                  </Box>
                </>
              )}

              <ToolbarTooltip label={t('chat.imageViewer.moreActions')}>
                <Pressable
                  onPress={() => setMoreVisible((v) => !v)}
                  style={({ pressed, hovered }: any) => [
                    s.iconBtn,
                    (pressed || hovered) && s.iconBtnHover,
                    moreVisible && s.iconBtnHover,
                  ]}
                  hitSlop={10}
                  accessibilityLabel={t('chat.imageViewer.moreActions')}>
                  <Images.MoreVertical
                    width={TOOLBAR_ICON}
                    height={TOOLBAR_ICON}
                    color={theme.colors.white}
                  />
                </Pressable>
              </ToolbarTooltip>
            </Box>
          </Box>

          {/* ── PILLS (below header) ───────────────────────────────────────────── */}
          {hasPills &&
            (() => {
              // Group pills so each project is followed by its child tasks
              const projects = linkedItems.filter((i) => i.type === 'project');
              const tasks = linkedItems.filter((i) => i.type === 'task');
              const orphanTasks = tasks.filter(
                (t) => !projects.some((p) => t.projectId === p.id)
              );
              const grouped: LinkedItem[] = [
                ...projects.flatMap((p) => [
                  p,
                  ...tasks.filter((t) => t.projectId === p.id),
                ]),
                ...orphanTasks,
              ];
              return (
                <Box style={s.pillBar}>
                  {grouped.map((item) => (
                    <Pressable
                      key={item.id}
                      ref={(r: any) => {
                        pillRefs.current[item.id] = r;
                      }}
                      style={[
                        s.pill,
                        item.type === 'project' ? s.pillProject : s.pillTask,
                      ]}
                      accessibilityLabel={t('chat.imageViewer.linkedTo', {
                        name: item.name,
                      })}
                      {...(Platform.OS === 'web' && item.name.length > 20
                        ? ({
                            onMouseEnter: () => {
                              pillRefs.current[item.id]?.measure?.(
                                (
                                  _x: number,
                                  _y: number,
                                  _w: number,
                                  h: number,
                                  pageX: number,
                                  pageY: number
                                ) => {
                                  setPillTooltipPos({
                                    x: pageX,
                                    y: pageY + h + 6,
                                  });
                                }
                              );
                              setHoveredPill(item.id);
                            },
                            onMouseLeave: () => {
                              setHoveredPill(null);
                              setPillTooltipPos(null);
                            },
                          } as any)
                        : {})}>
                      {item.type === 'project' ? (
                        <Images.Folder
                          width={12}
                          height={12}
                          color={theme.colors.white}
                        />
                      ) : (
                        <Images.Hash
                          width={12}
                          height={12}
                          color={theme.colors.white}
                        />
                      )}
                      <Text style={s.pillTxt} numberOfLines={1}>
                        {item.name.length > 20
                          ? item.name.slice(0, 20) + '…'
                          : item.name}
                      </Text>
                    </Pressable>
                  ))}
                </Box>
              );
            })()}

          {/* ── MORE DROPDOWN ─────────────────────────────────────────────────── */}
          {moreVisible && (
            <>
              <Pressable
                style={[StyleSheet.absoluteFill, { zIndex: 99 }]}
                onPress={() => setMoreVisible(false)}
              />
              <Box style={s.moreMenu}>
                <Pressable
                  style={s.moreItem}
                  onPress={handleOpenInfo}
                  accessibilityLabel={t('chat.imageViewer.fileInformation')}>
                  <Images.Info
                    width={16}
                    height={16}
                    color={theme.colors.white}
                  />
                  <Text style={s.moreItemTxt}>
                    {t('chat.imageViewer.information')}
                  </Text>
                </Pressable>
                {!isViewerOnly && (
                  <>
                    <Box style={s.moreDivider} />
                    {canDelete ? (
                      <Pressable
                        style={s.moreItem}
                        onPress={() => {
                          setMoreVisible(false);
                          setDeleteConfirm(true);
                        }}
                        accessibilityLabel={t('chat.imageViewer.deleteFile')}>
                        <Images.Trash2
                          width={16}
                          height={16}
                          color={theme.colors.alertRed}
                        />
                        <Text
                          style={[
                            s.moreItemTxt,
                            { color: theme.colors.alertRed },
                          ]}>
                          {t('chat.imageViewer.delete')}
                        </Text>
                      </Pressable>
                    ) : (
                      <Box style={[s.moreItem, { opacity: 0.4 }]}>
                        <Images.Trash2
                          width={16}
                          height={16}
                          color={theme.colors.alertRed}
                        />
                        <Text
                          style={[
                            s.moreItemTxt,
                            { color: theme.colors.alertRed },
                          ]}>
                          {t('chat.imageViewer.delete')}
                        </Text>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </>
          )}

          {/* ── HASH DROPDOWN ─────────────────────────────────────────────────── */}
          {hashVisible && (
            <>
              <Pressable
                style={[StyleSheet.absoluteFill, { zIndex: 199 }]}
                onPress={() => {
                  setHashVisible(false);
                  setHashSelected(new Set());
                  setHashSearch('');
                }}
              />
              <Box style={[s.hashMenu, { right: hashMenuRight }]}>
                {/* Search row */}
                <Box style={s.hashSearchRow}>
                  <Box style={s.hashSearchIcon}>
                    <Images.Hash
                      width={16}
                      height={16}
                      color={theme.colors.white}
                    />
                  </Box>
                  <TextInput
                    placeholder={t('chat.imageViewer.linkToProjectOrTask')}
                    placeholderTextColor={theme.colors.grey04}
                    style={s.hashSearchInput}
                    value={hashSearch}
                    onChangeText={setHashSearch}
                    autoFocus
                  />
                  <Pressable
                    onPress={() => {
                      setHashVisible(false);
                      setHashSelected(new Set());
                      setHashSearch('');
                    }}>
                    <Text style={s.hashCancelTxt}>
                      {t('chat.imageViewer.cancel')}
                    </Text>
                  </Pressable>
                </Box>
                <Box style={s.hashDivider} />

                <ScrollView
                  style={{ maxHeight: 360 }}
                  showsVerticalScrollIndicator>
                  {/* Recent */}
                  {recentItems.length > 0 && (
                    <>
                      <Text style={s.hashSectionLabel}>
                        {t('chat.imageViewer.recentProjectsAndTasks')}
                      </Text>
                      {filterItems(recentItems).map((item) => (
                        <HashRow
                          key={item.id}
                          item={item}
                          isChecked={hashSelected.has(item.id)}
                          onToggle={() => toggleHashItem(item)}
                        />
                      ))}
                      <Box style={s.hashDivider} />
                    </>
                  )}

                  {/* All */}
                  <Text style={s.hashSectionLabel}>
                    {t('chat.imageViewer.allProjectsAndTasks')}
                  </Text>
                  {filterItems(allItems).map((item) => (
                    <HashRow
                      key={`all-${item.id}`}
                      item={item}
                      isChecked={hashSelected.has(item.id)}
                      onToggle={() => toggleHashItem(item)}
                    />
                  ))}
                  <Box style={{ height: 12 }} />
                </ScrollView>

                <Box style={s.hashLinkBar}>
                  {(() => {
                    const unchanged =
                      hashSelected.size === hashInitialSel.size &&
                      Array.from(hashSelected).every((n) =>
                        hashInitialSel.has(n)
                      );
                    return (
                      <Pressable
                        disabled={unchanged || linking}
                        style={({ pressed, hovered }: any) => [
                          s.hashLinkBtn,
                          unchanged && s.hashLinkBtnDisabled,
                          !unchanged &&
                            !linking &&
                            (pressed || hovered) && { opacity: 0.85 },
                        ]}
                        onPress={handleLink}
                        accessibilityLabel={t(
                          'chat.imageViewer.linkSelectedItems'
                        )}>
                        {linking ? (
                          <ActivityIndicator
                            size='small'
                            color={theme.colors.white}
                            accessibilityLabel={t(
                              'chat.imageViewer.linkingInProgress'
                            )}
                          />
                        ) : (
                          <Text
                            style={[
                              s.hashLinkBtnTxt,
                              unchanged && s.hashLinkBtnTxtDisabled,
                            ]}>
                            {t('chat.imageViewer.linkButton')}
                          </Text>
                        )}
                      </Pressable>
                    );
                  })()}
                </Box>
              </Box>
            </>
          )}

          {/* ── IMAGE AREA ────────────────────────────────────────────────────── */}
          <Box
            testID='media-viewer-image-area'
            ref={imageAreaRef}
            style={[
              s.imageArea,
              Platform.OS === 'web' &&
                isActiveImage &&
                zoom > 100 &&
                ({
                  cursor: isPanning ? 'grabbing' : 'grab',
                  touchAction: 'none',
                } as any),
            ]}
            {...panHandlers}
            onLayout={(e) => {
              setImageAreaWidth(e.nativeEvent.layout.width);
              setImageAreaHeight(e.nativeEvent.layout.height);
              setPanOffset((current) =>
                clampPanOffset(
                  current,
                  zoom,
                  e.nativeEvent.layout.width,
                  e.nativeEvent.layout.height
                )
              );
            }}>
            {renderMediaContent()}

            {idx > 0 && (
              <Pressable
                onPress={() => navigateTo(idx - 1)}
                style={[s.navBtn, s.navLeft]}
                hitSlop={16}
                accessibilityLabel={t('chat.imageViewer.previousImage')}>
                <Images.ArrowLeft
                  width={NAV_ICON}
                  height={NAV_ICON}
                  color={theme.colors.white}
                />
              </Pressable>
            )}
            {(idx < previewDocuments.length - 1 || canLoadMore) && (
              <Pressable
                onPress={() => navigateTo(idx + 1)}
                style={[s.navBtn, s.navRight]}
                hitSlop={16}
                accessibilityLabel={t('chat.imageViewer.nextImage')}>
                {pendingAdvance ? (
                  <ActivityIndicator
                    size={NAV_ICON}
                    color={theme.colors.white}
                    accessibilityLabel='Loading more files'
                  />
                ) : (
                  <Images.ArrowRight
                    width={NAV_ICON}
                    height={NAV_ICON}
                    color={theme.colors.white}
                  />
                )}
              </Pressable>
            )}

            {/* Floating multi-select bar */}
            {!isViewerOnly && selMode && selCount > 0 && (
              <Box style={s.floatingSelBar}>
                <Box style={s.floatingSelBtns}>
                  <Pressable
                    onPress={handleDownload}
                    disabled={isDownloading}
                    onLayout={(e) => setDlBtnWidth(e.nativeEvent.layout.width)}
                    style={({ pressed, hovered }: any) => [
                      s.floatingSelBtn,
                      !isDownloading &&
                        (pressed || hovered) &&
                        s.floatingSelBtnHover,
                      isDownloading && { opacity: 0.6 },
                    ]}
                    accessibilityLabel={t('chat.imageViewer.downloadCount', {
                      count: selCount,
                    })}>
                    {isDownloading ? (
                      <ActivityIndicator
                        size={16}
                        color={theme.colors.white}
                        accessibilityLabel={t('chat.imageViewer.download')}
                      />
                    ) : (
                      <Images.Download
                        width={16}
                        height={16}
                        color={theme.colors.white}
                      />
                    )}
                    <Text style={s.floatingSelBtnTxt}>
                      {t('chat.imageViewer.download')}
                    </Text>
                    <Box style={s.floatingSelBadge}>
                      <Text style={s.floatingSelBadgeTxt}>{selCount}</Text>
                    </Box>
                  </Pressable>
                  <Pressable
                    ref={fwdFloatingRef}
                    onPress={() => {
                      if (fwdFloatingRef.current && Platform.OS === 'web') {
                        fwdFloatingRef.current.measure(
                          (
                            _x: number,
                            _y: number,
                            width: number,
                            _h: number,
                            pageX: number,
                            pageY: number
                          ) => {
                            setFwdMenuRight(
                              (window as any).innerWidth - pageX - width
                            );
                            setFwdMenuTop(undefined);
                            setFwdMenuBottom(
                              (window as any).innerHeight - pageY + 8
                            );
                            setFwdVisible((v) => !v);
                          }
                        );
                      } else {
                        setFwdVisible((v) => !v);
                      }
                    }}
                    style={({ pressed, hovered }: any) => [
                      s.floatingSelBtn,
                      (pressed || hovered) && s.floatingSelBtnHover,
                      dlBtnWidth ? { width: dlBtnWidth } : undefined,
                    ]}
                    accessibilityLabel={t('chat.imageViewer.forwardCount', {
                      count: selCount,
                    })}>
                    <Images.ForwardStroke
                      width={16}
                      height={16}
                      color={theme.colors.white}
                    />
                    <Text style={s.floatingSelBtnTxt}>
                      {t('chat.imageViewer.forward')}
                    </Text>
                    <Box style={s.floatingSelBadge}>
                      <Text style={s.floatingSelBadgeTxt}>{selCount}</Text>
                    </Box>
                  </Pressable>
                  <Pressable
                    onPress={() => {
                      setSelMode(false);
                      setSelected(new Set());
                    }}
                    style={({ pressed, hovered }: any) => [
                      s.floatingCancelBtn,
                      (pressed || hovered) && s.floatingCancelBtnHover,
                    ]}
                    hitSlop={8}
                    accessibilityLabel={t('chat.imageViewer.cancelSelection')}>
                    <Images.X
                      width={18}
                      height={18}
                      color={theme.colors.white}
                    />
                  </Pressable>
                </Box>
              </Box>
            )}
          </Box>

          {/* ── BOTTOM THUMBNAIL STRIP ────────────────────────────────────────── */}
          {previewDocuments.length > 1 && (
            <Box style={s.bottomBar}>
              <ScrollView
                ref={thumbScrollRef}
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={s.thumbStrip}
                onScroll={onThumbStripScroll}
                scrollEventThrottle={16}
                testID='media-viewer-thumb-strip'
                style={{ height: BOTTOM_H }}>
                {previewDocuments.map((item, i) => (
                  <Pressable
                    key={item.id ?? `${i}`}
                    onPress={() => navigateTo(i)}
                    style={{ position: 'relative' }}
                    {...(Platform.OS === 'web'
                      ? ({
                          onMouseEnter: () => setHoveredThumb(i),
                          onMouseLeave: () => setHoveredThumb(null),
                        } as any)
                      : {})}>
                    <Box
                      style={[
                        s.thumbBorder,
                        !selMode && i === idx && s.thumbActive,
                        selMode && selected.has(i) && s.thumbSelected,
                      ]}>
                      <Box style={s.thumbWrap}>
                        {(() => {
                          const isVid =
                            item.isVideo || item.contentType?.includes('video');
                          const isImg =
                            item.isImage ||
                            item.contentType?.includes('image/');
                          if (isVid) {
                            const thumbUri = item.file?.thumbnail ?? null;
                            return thumbUri ? (
                              <Image
                                source={{ uri: thumbUri }}
                                style={s.thumb}
                                resizeMode='cover'
                              />
                            ) : null;
                          } else if (isImg) {
                            // Prefer the dedicated thumbnail; resolveUri now
                            // returns the full-res CDN URL (TA-9771), which is
                            // wasteful at thumb size.
                            const thumbUri =
                              item.file?.thumbnail ?? resolveUri(item);
                            return thumbUri ? (
                              <Image
                                source={{ uri: thumbUri }}
                                style={s.thumb}
                                resizeMode='cover'
                              />
                            ) : null;
                          } else {
                            return (
                              <Box
                                style={[s.thumb, s.thumbFileBg]}
                                alignItems='center'
                                justifyContent='center'>
                                <Icon
                                  fileType={item.contentType || undefined}
                                  color='blue'
                                  variant='s'
                                />
                              </Box>
                            );
                          }
                        })()}
                        {hoveredThumb === i && !selected.has(i) && (
                          <Box style={s.thumbHoverOverlay} />
                        )}
                      </Box>
                    </Box>
                    {!isViewerOnly && (
                      <Pressable
                        onPress={(e: any) => {
                          // Prevent the parent thumbnail Pressable from also firing
                          // navigateTo(i) when the user taps the check circle.
                          e?.stopPropagation?.();
                          if (!selMode) setSelMode(true);
                          toggleSelect(i);
                        }}
                        style={[
                          s.checkCircle,
                          selected.has(i) && s.checkCircleSelected,
                        ]}
                        hitSlop={4}
                        accessibilityLabel={
                          selected.has(i)
                            ? t('chat.imageViewer.deselectImage', {
                                index: i + 1,
                              })
                            : t('chat.imageViewer.selectImage', {
                                index: i + 1,
                              })
                        }>
                        {selected.has(i) && (
                          <Images.Check
                            width={14}
                            height={14}
                            color={theme.colors.white}
                          />
                        )}
                      </Pressable>
                    )}
                  </Pressable>
                ))}
                {canLoadMore && (
                  <Box
                    style={[s.thumbBorder, { justifyContent: 'center' }]}
                    testID='thumb-strip-loading-more'>
                    <ActivityIndicator color={theme.colors.white} />
                  </Box>
                )}
              </ScrollView>
            </Box>
          )}

          {/* ── FORWARD DROPDOWN ──────────────────────────────────────────────── */}
          {fwdVisible && (
            <>
              <Pressable
                style={[StyleSheet.absoluteFill, { zIndex: 199 }]}
                onPress={() => setFwdVisible(false)}
              />
              <Box
                style={[
                  s.fwdMenu,
                  {
                    right: fwdMenuRight,
                    top: fwdMenuTop,
                    bottom: fwdMenuBottom,
                  },
                ]}>
                <ChatImageForwardPicker
                  attachments={
                    selMode && selected.size > 0
                      ? Array.from(selected)
                          .map((i) => previewDocuments[i])
                          .filter(Boolean)
                      : [doc]
                  }
                  onCancel={() => setFwdVisible(false)}
                  onComplete={() => setFwdVisible(false)}
                />
              </Box>
            </>
          )}

          {/* ── FILE INFO MODAL ───────────────────────────────────────────────── */}
          {infoVisible && (
            <Box style={s.confirmOverlay}>
              <Box style={s.confirmBox}>
                <Box style={s.confirmHeader}>
                  <Text style={s.confirmTitle}>
                    {t('chat.imageViewer.fileDetails')}
                  </Text>
                  <Pressable
                    onPress={() => setInfoVisible(false)}
                    hitSlop={8}
                    style={({ pressed, hovered }: any) => [
                      s.confirmClose,
                      (pressed || hovered) && { opacity: 0.6 },
                    ]}
                    accessibilityLabel={t('chat.imageViewer.closeFileDetails')}>
                    <Images.X
                      width={18}
                      height={18}
                      color={theme.colors.textPrimary}
                    />
                  </Pressable>
                </Box>
                <InfoRow
                  label={t('chat.imageViewer.infoName')}
                  value={filename}
                />
                <InfoRow
                  label={t('chat.imageViewer.infoType')}
                  value={t(
                    doc?.isVideo || doc?.contentType?.includes('video')
                      ? 'chat.imageViewer.infoTypeVideo'
                      : 'chat.imageViewer.infoTypeImage'
                  )}
                />
                <InfoRow
                  label={t('chat.imageViewer.infoSize')}
                  value={imgSize}
                />
                <InfoRow
                  label={t('chat.imageViewer.infoDimensions')}
                  value={
                    imgDimensions
                      ? t('chat.imageViewer.dimensionsFormat', {
                          width: imgDimensions.width,
                          height: imgDimensions.height,
                        })
                      : t('chat.imageViewer.infoDash')
                  }
                />
                <InfoRow
                  label={t('chat.imageViewer.infoOwner')}
                  value={doc.owner?.name ?? t('chat.imageViewer.infoDash')}
                />
                <InfoRow
                  label={t('chat.imageViewer.infoCreated')}
                  value={
                    doc.createdAt
                      ? dayjs(doc.createdAt).format('MMM D, YYYY h:mm A')
                      : t('chat.imageViewer.infoDash')
                  }
                  last={!imgModifiedAt}
                />
                {/* Modified row only renders when the viewer-only lazy query
                  succeeds. On older backends it stays hidden so this PR is
                  safe to ship before the BE rollout completes. */}
                {imgModifiedAt && (
                  <InfoRow
                    label={t('chat.imageViewer.infoModified')}
                    value={dayjs(imgModifiedAt).format('MMM D, YYYY h:mm A')}
                    last
                  />
                )}
              </Box>
            </Box>
          )}

          {/* ── DELETE CONFIRM ────────────────────────────────────────────────── */}
          {deleteConfirm && (
            <Box style={s.confirmOverlay}>
              <Box style={s.confirmBox}>
                <Box style={s.confirmHeader}>
                  <Text style={s.confirmTitle}>
                    {t('chat.imageViewer.deleteConfirmTitle')}
                  </Text>
                  <Pressable
                    onPress={() => setDeleteConfirm(false)}
                    hitSlop={8}
                    style={({ pressed, hovered }: any) => [
                      s.confirmClose,
                      (pressed || hovered) && { opacity: 0.6 },
                    ]}
                    accessibilityLabel={t(
                      'chat.imageViewer.closeDeleteDialog'
                    )}>
                    <Images.X
                      width={18}
                      height={18}
                      color={theme.colors.textPrimary}
                    />
                  </Pressable>
                </Box>
                <Text style={s.confirmBody}>
                  {t('chat.imageViewer.deleteConfirmBody')}
                </Text>
                <Box style={s.confirmActions}>
                  <Pressable
                    onPress={handleDelete}
                    style={({ pressed, hovered }: any) => [
                      s.deleteBtn,
                      (pressed || hovered) && { opacity: 0.85 },
                    ]}
                    accessibilityLabel={t('chat.imageViewer.confirmDelete')}>
                    <Text style={s.deleteBtnTxt}>
                      {t('chat.imageViewer.deleteConfirmButton')}
                    </Text>
                  </Pressable>
                </Box>
              </Box>
            </Box>
          )}

          {/* ── PILL HOVER TOOLTIP (global layer) ─────────────────────────────── */}
          {hoveredPill &&
            pillTooltipPos &&
            (() => {
              const item = linkedItems.find((i) => i.id === hoveredPill);
              if (!item) return null;
              return (
                <Box
                  pointerEvents='none'
                  style={[
                    s.pillTooltip,
                    {
                      position: 'absolute',
                      left: pillTooltipPos.x,
                      top: pillTooltipPos.y,
                    },
                  ]}>
                  <Text style={s.pillTooltipTxt}>{item.name}</Text>
                </Box>
              );
            })()}
        </Box>
      </ToastProvider>
    </Modal>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────
// ToolbarTooltip lives in its own module (TA-10293) so the guest
// ImagesPreviewNotLogin.web.tsx viewer can reuse the same hover-tooltip
// styling on its download/zoom controls.

const HashRow: React.FC<{
  item: HashItem;
  isChecked: boolean;
  onToggle: () => void;
}> = ({ item, isChecked, onToggle }) => {
  const { t } = useTranslation('models');
  return (
    <Pressable
      style={({ pressed, hovered }: any) => [
        s.hashItem,
        (pressed || hovered) && s.hashItemHover,
      ]}
      onPress={onToggle}
      accessibilityLabel={t(
        isChecked
          ? 'chat.imageViewer.deselectName'
          : 'chat.imageViewer.selectName',
        { name: item.name }
      )}>
      <Box
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.s,
          flex: 1,
          minWidth: 0,
        }}>
        <Box style={[s.hashCheckCircle, isChecked && s.hashCheckCircleActive]}>
          {isChecked && (
            <Images.Check width={12} height={12} color={theme.colors.white} />
          )}
        </Box>
        {item.type === 'project' ? (
          <Box style={s.hashProjectIcon}>
            <Text style={s.hashProjectIconTxt}>tt</Text>
          </Box>
        ) : (
          <Box style={s.hashTaskIcon}>
            <Images.Hash
              width={20}
              height={20}
              color={theme.colors.greenSecondary}
            />
          </Box>
        )}
        <Text
          style={[
            s.hashItemTxt,
            item.type === 'project' && { fontWeight: '700' },
          ]}
          numberOfLines={1}>
          {item.name}
        </Text>
      </Box>
    </Pressable>
  );
};

const InfoRow: React.FC<{ label: string; value: string; last?: boolean }> = ({
  label,
  value,
  last,
}) => (
  <Box style={[s.infoRow, last && { marginBottom: 0 }]}>
    <Text style={s.infoRowLabel}>{label}</Text>
    <Text style={s.infoRowValue} numberOfLines={2}>
      {value}
    </Text>
  </Box>
);

// ── Styles ────────────────────────────────────────────────────────────────────
// root/header/hLeft/hCenter/hRight/senderBlock/senderName/dateLabel/
// filenameRow/filename/iconBtn/iconBtnHover/iconGroup/groupDivider/zoomPct/
// imageArea/image/navBtn/navLeft/navRight/bottomBar/thumbStrip/thumbBorder/
// thumbActive/thumbWrap/thumb/thumbFileBg/thumbHoverOverlay come from
// chromeStyles (TA-10293) so this viewer and the guest
// ImagesPreviewNotLogin.web.tsx viewer can't drift apart on shared chrome.
const s = StyleSheet.create({
  ...chromeStyles,
  renameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xxs,
  },
  renameInput: {
    color: theme.colors.white,
    fontSize: 13,
    fontWeight: '500',
    backgroundColor: theme.colors.scrimLight08,
    borderRadius: 6,
    paddingHorizontal: theme.spacing.xs,
    paddingVertical: 3,
    borderWidth: 1,
    borderColor: theme.colors.white,
    minWidth: 120,
    maxWidth: 220,
    // Opts back out of the header's `userSelect: 'none'` (TA-10615) — the
    // rename field needs a real caret and selection, and `selectTextOnFocus`
    // above depends on it.
    ...(Platform.OS === 'web'
      ? ({ outlineStyle: 'none', userSelect: 'text' } as any)
      : {}),
  },

  // Icon buttons
  iconBtn24: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
  },

  // Pills
  pillBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: 6,
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.xs,
    backgroundColor: theme.colors.viewerChrome,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: theme.colors.scrimLight08,
  },
  pill: {
    borderRadius: 8,
    paddingHorizontal: 10,
    height: 28,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    flexShrink: 0,
  },
  pillProject: {
    backgroundColor: theme.colors.greenSecondary,
  },
  pillTask: {
    backgroundColor: theme.colors.transparentBlack,
  },
  pillTxt: {
    color: theme.colors.white,
    fontSize: 12,
    fontWeight: '400',
  },

  // More menu
  moreMenu: {
    position: 'absolute',
    top: 52,
    right: 8,
    backgroundColor: theme.colors.textPrimary,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: theme.colors.scrimLight10,
    minWidth: 220,
    paddingVertical: theme.spacing.xxs,
    zIndex: 100,
    ...(Platform.OS === 'web'
      ? ({ boxShadow: `0 8px 24px ${theme.colors.scrimDark50}` } as any)
      : {}),
  },
  moreItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 14,
    paddingVertical: 11,
  },
  moreItemTxt: {
    color: theme.colors.white,
    fontSize: 14,
    flex: 1,
  },
  moreDivider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: theme.colors.scrimLight10,
    marginVertical: theme.spacing.xxs,
  },

  filePreviewBox: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.l,
  },

  // Floating selection bar
  floatingSelBar: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 10,
  },
  floatingSelBtns: {
    flexDirection: 'row',
    gap: 10,
  },
  floatingSelBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: 18,
    paddingVertical: 10,
    backgroundColor: theme.colors.viewerFloatingBtnBg,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: theme.colors.scrimLight15,
  },
  floatingSelBtnHover: {
    backgroundColor: theme.colors.viewerFloatingBtnBgHover,
    borderColor: theme.colors.scrimLight25,
  },
  floatingSelBtnTxt: {
    color: theme.colors.white,
    fontSize: 13,
    fontWeight: '600',
  },
  floatingSelBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: theme.colors.greenSecondary,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.xxs,
  },
  floatingSelBadgeTxt: {
    color: theme.colors.white,
    fontSize: 11,
    fontWeight: '700',
    lineHeight: 14,
    includeFontPadding: false,
  },
  floatingCancelBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.viewerFloatingBtnBg,
    borderWidth: 1,
    borderColor: theme.colors.scrimLight15,
  },
  floatingCancelBtnHover: {
    backgroundColor: theme.colors.viewerFloatingBtnBgHover,
    borderColor: theme.colors.scrimLight25,
  },

  // Bottom bar — multi-select-only additions; the base strip styles
  // (bottomBar/thumbStrip/thumbBorder/thumbActive/thumbWrap/thumb/
  // thumbFileBg/thumbHoverOverlay) come from chromeStyles above.
  thumbSelected: {
    borderColor: theme.colors.white,
    opacity: 0.85,
  },
  checkCircle: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: theme.colors.white,
    backgroundColor: theme.colors.scrimDark25,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkCircleSelected: {
    backgroundColor: theme.colors.greenSecondary,
    borderColor: theme.colors.greenSecondary,
  },

  // Hash dropdown
  hashMenu: {
    position: 'absolute',
    top: 56,
    width: 560,
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadii.m,
    zIndex: 200,
    overflow: 'hidden',
    ...(Platform.OS === 'web'
      ? ({ boxShadow: `0 8px 32px ${theme.colors.scrimDark35}` } as any)
      : {}),
  },
  hashSearchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.m,
    gap: 10,
  },
  hashSearchIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: theme.colors.greenSecondary,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  hashSearchInput: {
    flex: 1,
    height: 40,
    fontSize: 15,
    color: theme.colors.black,
    ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {}),
  },
  hashCancelTxt: {
    color: theme.colors.textSecondary,
    fontSize: 15,
    fontWeight: '600',
  },
  hashDivider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: theme.colors.scrimDark10,
  },
  hashSectionLabel: {
    color: theme.colors.grey04,
    fontSize: 13,
    paddingHorizontal: theme.spacing.m,
    paddingTop: 14,
    paddingBottom: 6,
  },
  hashItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.m,
    paddingVertical: 10,
  },
  hashItemHover: {
    backgroundColor: theme.colors.scrimDark04,
  },
  hashCheckCircle: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: theme.colors.scrimDark20,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  hashCheckCircleActive: {
    backgroundColor: theme.colors.greenSecondary,
    borderColor: theme.colors.greenSecondary,
  },
  hashProjectIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: theme.colors.black,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  hashProjectIconTxt: {
    color: theme.colors.white,
    fontSize: 13,
    fontWeight: '700',
    fontStyle: 'italic',
  },
  hashTaskIcon: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  hashItemTxt: {
    color: theme.colors.textSecondary,
    fontSize: 15,
    flex: 1,
  },
  hashLinkBar: {
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.s,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: theme.colors.scrimDark10,
  },
  hashLinkBtn: {
    backgroundColor: theme.colors.greenSecondary,
    borderRadius: 8,
    paddingVertical: 10,
    alignItems: 'center',
  },
  hashLinkBtnDisabled: {
    backgroundColor: theme.colors.scrimDark08,
  },
  hashLinkBtnTxt: {
    color: theme.colors.white,
    fontSize: 15,
    fontWeight: '600',
  },
  hashLinkBtnTxtDisabled: {
    color: theme.colors.scrimDark30,
  },

  // Forward dropdown (anchored to forward button, ~400px wide)
  fwdMenu: {
    position: 'absolute',
    width: 420,
    maxHeight: 520,
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadii.m,
    zIndex: 200,
    overflow: 'hidden',
    ...(Platform.OS === 'web'
      ? ({ boxShadow: `0 8px 32px ${theme.colors.scrimDark35}` } as any)
      : {}),
  },

  // Confirm / Info overlays
  confirmOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.colors.grey06,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9000,
  },
  confirmBox: {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadii.m,
    padding: theme.spacing.m,
    width: '100%',
    maxWidth: 480,
    ...(Platform.OS === 'web'
      ? ({ boxShadow: `0 8px 32px ${theme.colors.scrimDark25}` } as any)
      : {}),
  },
  confirmHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.s,
  },
  confirmTitle: {
    color: theme.colors.textPrimary,
    fontSize: 18,
    fontWeight: '700',
  },
  confirmClose: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
  },
  confirmBody: {
    color: theme.colors.grey04,
    fontSize: 14,
    marginBottom: theme.spacing.l,
  },
  confirmActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: theme.spacing.s,
  },
  deleteBtn: {
    backgroundColor: theme.colors.textPrimary,
    borderRadius: 8,
    paddingHorizontal: theme.spacing.m,
    paddingVertical: 10,
    alignSelf: 'flex-end',
  },
  deleteBtnTxt: {
    color: theme.colors.white,
    fontSize: 14,
    fontWeight: '600',
  },

  // Info modal rows (column layout: label above value)
  infoRow: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginBottom: theme.spacing.m,
  },
  infoRowLabel: {
    color: theme.colors.grey06,
    fontSize: 12,
    marginBottom: theme.spacing.xxs,
  },
  infoRowValue: {
    color: theme.colors.textSecondary,
    fontSize: 14,
  },

  // Pill hover tooltip (truncated names)
  pillTooltip: {
    backgroundColor: theme.colors.black,
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
    zIndex: 9999,
    ...(Platform.OS === 'web'
      ? ({
          boxShadow: `0 2px 8px ${theme.colors.scrimDark25}`,
          whiteSpace: 'nowrap',
          width: 'max-content',
        } as any)
      : {}),
  },
  pillTooltipTxt: {
    color: theme.colors.white,
    fontSize: 12,
  },

  // Text styling for the "Link copied!" content passed as ToolbarTooltip's
  // `customContent` — the anchored bubble itself now lives in
  // ToolbarTooltip.web.tsx (TA-10293).
  linkCopiedTxt: {
    color: theme.colors.textSecondary,
    fontSize: 12,
    lineHeight: 18,
    fontWeight: '500',
  },
});
